import { useAmendmentHistory } from "../hooks/useSowingLifecycle";
import type { SowingInstructionAmendmentDto } from "../services/sowingLifecycleApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  Pending:  { label: "Pending",  className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  Approved: { label: "Approved", className: "bg-green-100 text-green-800 border-green-200" },
  Rejected: { label: "Rejected", className: "bg-red-100 text-red-800 border-red-200" },
};

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] ?? { label: status, className: "" };
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "dd MMM yyyy");
  } catch {
    return "—";
  }
}

function QtyChange({ amendment }: { amendment: SowingInstructionAmendmentDto }) {
  const changed = amendment.priorQuantityToSow !== amendment.newQuantityToSow;
  return (
    <span>
      {changed ? (
        <>
          <span className="text-muted-foreground line-through mr-1">
            {amendment.priorQuantityToSow}
          </span>
          <span className="font-semibold">{amendment.newQuantityToSow}</span>
        </>
      ) : (
        <span>{amendment.newQuantityToSow}</span>
      )}
    </span>
  );
}

export const AmendmentHistoryPage = () => {
  const historyQuery = useAmendmentHistory();
  const rows = historyQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Amendment History</h1>
        <p className="text-muted-foreground mt-1">
          All sowing instruction amendments — pending, approved, and rejected.
        </p>
      </div>

      {historyQuery.isLoading && (
        <div className="flex items-center justify-center h-64">
          <p>Loading amendment history...</p>
        </div>
      )}

      {historyQuery.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-600">Failed to load amendment history</p>
        </div>
      )}

      {!historyQuery.isLoading && rows.length === 0 && (
        <div className="rounded-lg border bg-muted/40 p-8 text-center">
          <p className="text-muted-foreground">No amendments have been proposed yet</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instruction #</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Proposed By</TableHead>
                <TableHead>Proposed At</TableHead>
                <TableHead>New Qty</TableHead>
                <TableHead>New Start</TableHead>
                <TableHead>New End</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Decided By</TableHead>
                <TableHead>Decided At</TableHead>
                <TableHead>Decision Comment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((amendment) => (
                <TableRow key={amendment.id}>
                  <TableCell className="font-mono text-sm">
                    {amendment.instructionNumber ?? "—"}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={amendment.status} />
                  </TableCell>
                  <TableCell>{amendment.amendedByUserName ?? "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(amendment.amendedAt)}
                  </TableCell>
                  <TableCell>
                    <QtyChange amendment={amendment} />
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(amendment.newScheduledStartDate)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(amendment.newScheduledEndDate)}
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm">
                    {amendment.reason || "—"}
                  </TableCell>
                  <TableCell>{amendment.decisionByUserName ?? "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(amendment.decisionAt)}
                  </TableCell>
                  <TableCell className="max-w-[160px] truncate text-sm text-muted-foreground">
                    {amendment.decisionComment || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
